import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import type { AuditLogItem } from '../../lib/admin-client.js';

const s = {
  row: {
    borderBottom: '1px solid rgba(55,65,81,0.5)',
    cursor: 'pointer',
  },
  td: {
    padding: '0.625rem 0.75rem',
    fontSize: '0.875rem',
    color: tokens.colors.textDark,
    verticalAlign: 'top' as const,
  },
  expanded: {
    padding: '0.75rem 1rem',
    backgroundColor: '#0f172a',
    borderBottom: '1px solid rgba(55,65,81,0.5)',
  },
  diffContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  diffTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    marginBottom: '0.375rem',
    letterSpacing: '0.05em',
  },
  pre: {
    backgroundColor: '#1a1f2e',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: '4px',
    padding: '0.75rem',
    fontSize: '0.75rem',
    color: '#e2e8f0',
    fontFamily: 'monospace',
    overflow: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    margin: 0,
  },
};

interface AuditLogRowProps {
  entry: AuditLogItem;
  isOdd?: boolean;
}

export function AuditLogRow({ entry, isOdd = false }: AuditLogRowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasDiff = entry.before != null || entry.after != null;

  return (
    <>
      <tr
        style={{ ...s.row, backgroundColor: isOdd ? 'rgba(17,24,39,0.3)' : 'transparent' }}
        onClick={() => hasDiff && setExpanded((p) => !p)}
        onKeyDown={(e) => {
          if (hasDiff && (e.key === 'Enter' || e.key === ' ')) setExpanded((p) => !p);
        }}
        aria-expanded={hasDiff ? expanded : undefined}
      >
        <td
          style={{
            ...s.td,
            color: tokens.colors.textMutedDark,
            fontSize: '0.8125rem',
            whiteSpace: 'nowrap',
          }}
        >
          {new Date(entry.occurred_at).toLocaleString()}
        </td>
        <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
          {entry.actor_email_snapshot}
        </td>
        <td style={{ ...s.td }}>
          <span
            style={{
              backgroundColor: '#1e3a5f',
              color: '#93c5fd',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontFamily: 'monospace',
            }}
          >
            {entry.action}
          </span>
        </td>
        <td style={{ ...s.td, color: tokens.colors.textMutedDark, fontSize: '0.8125rem' }}>
          {entry.resource_type}
        </td>
        <td
          style={{
            ...s.td,
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            color: tokens.colors.textMutedDark,
          }}
        >
          {entry.resource_id ?? '—'}
        </td>
        <td style={{ ...s.td, textAlign: 'center' as const }}>
          {hasDiff && (
            <span style={{ color: tokens.colors.textMutedDark, fontSize: '0.75rem' }}>
              {expanded ? '▲' : '▼'}
            </span>
          )}
        </td>
      </tr>
      {expanded && hasDiff && (
        <tr>
          <td colSpan={6} style={s.expanded}>
            <div style={s.diffContainer}>
              <div>
                <p style={s.diffTitle}>Before</p>
                <pre style={s.pre}>{JSON.stringify(entry.before, null, 2)}</pre>
              </div>
              <div>
                <p style={s.diffTitle}>After</p>
                <pre style={s.pre}>{JSON.stringify(entry.after, null, 2)}</pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
